/**
 * App-wide sync: when the ongoing job API returns a job_id, rehydrate the
 * security scan store so progress is restored after page refresh.
 * Renders nothing; mount once in MainLayout.
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/auth/useAuth';
import { useOrg } from '@/hooks/org/useOrg';
import { useSecurityOngoingJob } from '@/hooks/security-scanner';
import { useSecurityScanStore } from '@/stores/securityScanStore';

export function SecurityOngoingJobSync() {
  const { user } = useAuth();
  const { selectedOrg } = useOrg();
  const orgId = selectedOrg?.id ?? '';
  const userId = user?.id ?? '';
  const { data } = useSecurityOngoingJob({
    org_id: orgId || undefined,
    user_id: userId || undefined,
  });
  const rehydratedJobIdRef = useRef<string | null>(null);
  const rehydrateFromOngoingJob = useSecurityScanStore(
    (s) => s.rehydrateFromOngoingJob
  );

  useEffect(() => {
    const jobId = data?.job_id;
    if (!jobId || !orgId || !userId) return;
    const { isScanning, jobId: currentJobId } = useSecurityScanStore.getState().state;
    if (isScanning && currentJobId === jobId) return;
    if (rehydratedJobIdRef.current === jobId) return;

    rehydratedJobIdRef.current = jobId;
    rehydrateFromOngoingJob(orgId, String(userId), jobId);
  }, [data?.job_id, orgId, userId, rehydrateFromOngoingJob]);

  return null;
}
